import type { LayerName } from '../energy/layers';

/**
 * Il registro delle metriche esplodibili per POD.
 *
 * Ogni KPI e ogni grafico dell'app dichiara un id di metrica, e questo registro
 * è l'unico posto che risponde alle due domande che servono per esploderlo: come
 * si scompone per POD, e come va presentato (etichetta, unità, direzione).
 *
 * ── La base non è più una proprietà della metrica ───────────────────────────
 * Lo era finché convivevano due ordini di priorità e ogni esploso doveva leggere
 * la stessa sorgente del KPI da cui si apriva, al prezzo che lo stesso POD
 * mostrava un livello vincente diverso da pannelli diversi. Dal 20260811 l'ordine
 * è uno solo — GSE › reale › stimato › tipico, in `@slims/shared/energy` — e
 * `basis` non ha più niente da scegliere.
 *
 * ── Perché `decomposition` non è sempre 'sum' ───────────────────────────────
 * Non tutto si scompone sommando. L'energia condivisa è un `min()` fra totali di
 * comunità e l'incentivo è distribuito per socio: per queste il valore per POD
 * esiste solo dentro una convenzione di riparto, e va etichettato come tale
 * invece che presentato come misura. `'none'` è la risposta onesta dove non
 * esiste alcun valore per POD (meteo, conteggi di piattaforma).
 */

export type MetricDecomposition =
	/** Il totale è la somma dei POD: l'esploso quadra per costruzione. */
	| 'sum'
	/** Il valore per POD esiste solo dentro una convenzione, e va dichiarata. */
	| 'allocated'
	/** Non esiste un valore per POD: il pannello non si esplode. */
	| 'none';

/** Come si ricava il valore per POD, una volta scelta la base. */
export type MetricCompute =
	/** Somma dei kWh della direzione sulla finestra richiesta. */
	| { kind: 'energy_sum' }
	/** Derivazione sulla serie oraria per POD (la stessa del KPI aggregato). */
	| { kind: 'energy_derived'; derive: 'cagr' | 'yoy' | 'monthly_avg' | 'bands' }
	/** Scostamento fra due livelli dello stesso POD, es. stimato contro reale. */
	| { kind: 'layer_delta'; left: LayerName; right: LayerName }
	/** Serie temporale per POD al grano richiesto dal grafico. */
	| { kind: 'energy_series'; bucket: 'slot' | 'hour' | 'day' | 'month' }
	/** Quota di un aggregato non additivo (autoconsumo, energia condivisa). */
	| { kind: 'ratio_allocated' }
	/** Conteggio di righe per POD (anomalie aperte, POD coinvolti). */
	| { kind: 'row_count' };

export type MetricDirection = 'consumo' | 'produzione' | 'both';

export interface MetricDef {
	/** Id stabile, usato nell'URL dell'endpoint e nella prop del pannello. */
	id: string;
	/** Titolo dell'esploso — non necessariamente identico a quello del pannello. */
	label: string;
	unit: 'kWh' | 'kW' | '€' | '%' | 'kg' | 'n';
	direction: MetricDirection;
	decomposition: MetricDecomposition;
	compute: MetricCompute;
	/**
	 * Presente solo su `decomposition: 'allocated'`: la frase che spiega la
	 * convenzione, mostrata nell'esploso sotto al badge «quota allocata». Senza
	 * questa un numero ripartito si legge come un numero misurato.
	 */
	allocationBasis?: string;
	/** Presente solo su `decomposition: 'none'`: perché non si esplode. */
	noExplodeReason?: string;
	/** Nota mostrata nell'esploso, es. per distinguere una previsione da una misura. */
	note?: string;
}

const RIPARTO_KWH = 'quota proporzionale ai kWh condivisi del POD';

const EURO_NON_PER_POD =
	"Gli euro sono della configurazione: non c'è un riparto per socio da cui ricavare un valore per POD. " +
	"Per la scomposizione usa l'energia condivisa.";

/**
 * Le metriche, raggruppate come i cluster della UI. L'ordine non conta: la
 * ricerca è per id.
 */
export const METRICS: readonly MetricDef[] = [
	// ── Consumi di configurazione (KPI e grafici da energy_shared) ─────────────
	{
		id: 'consumo_mensile',
		label: 'Consumo',
		unit: 'kWh',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_sum' },
	},
	{
		id: 'produzione_mensile',
		label: 'Produzione',
		unit: 'kWh',
		direction: 'produzione',
		decomposition: 'sum',
		compute: { kind: 'energy_sum' },
	},
	{
		id: 'immessa_mensile',
		label: 'Energia immessa in rete',
		unit: 'kWh',
		direction: 'produzione',
		decomposition: 'sum',
		compute: { kind: 'energy_sum' },
	},
	{
		id: 'consumo_trend_12m',
		label: 'Trend consumi · ultimi 12 mesi',
		unit: 'kWh',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'month' },
	},
	{
		id: 'consumo_cagr',
		label: 'CAGR consumi',
		unit: '%',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_derived', derive: 'cagr' },
	},
	{
		id: 'consumo_yoy',
		label: 'Confronto anno su anno',
		unit: '%',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_derived', derive: 'yoy' },
	},
	{
		id: 'consumo_media_mensile',
		label: 'Media mensile',
		unit: 'kWh',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_derived', derive: 'monthly_avg' },
	},
	{
		id: 'consumo_fasce_gse',
		label: 'Consumo per fascia oraria GSE',
		unit: 'kWh',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_derived', derive: 'bands' },
	},
	{
		id: 'consumo_profilo_orario',
		label: 'Profilo orario · produzione vs consumo',
		unit: 'kWh',
		direction: 'both',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'hour' },
	},
	{
		id: 'prod_cons_mensile_12m',
		label: 'Produzione vs consumo · 12 mesi',
		unit: 'kWh',
		direction: 'both',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'month' },
	},

	// ── Dashboard ─────────────────────────────────────────────────────────────
	{
		id: 'produzione_istantanea',
		label: 'Produzione istantanea',
		unit: 'kW',
		direction: 'produzione',
		decomposition: 'sum',
		compute: { kind: 'energy_sum' },
		note: 'Ultimo bucket orario disponibile, riportato a potenza media.',
	},
	{
		id: 'consumo_istantaneo',
		label: 'Consumo di configurazione',
		unit: 'kW',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_sum' },
	},
	{
		id: 'curva_produzione_consumo',
		label: 'Produzione vs consumo',
		unit: 'kWh',
		direction: 'both',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'hour' },
	},
	{
		id: 'autoconsumo_pct',
		label: 'Autoconsumo di configurazione',
		unit: '%',
		direction: 'both',
		decomposition: 'allocated',
		compute: { kind: 'ratio_allocated' },
		allocationBasis:
			"l'energia condivisa è un min() fra i totali di comunità: la quota per POD segue il " +
			RIPARTO_KWH,
	},
	{
		id: 'co2_risparmiata',
		label: 'CO₂ risparmiata',
		unit: 'kg',
		direction: 'both',
		decomposition: 'allocated',
		compute: { kind: 'ratio_allocated' },
		allocationBasis: `derivata dall'energia condivisa, quindi ${RIPARTO_KWH}`,
	},

	// ── Condivisione ──────────────────────────────────────────────────────────
	{
		id: 'energia_condivisa',
		label: 'Energia condivisa',
		unit: 'kWh',
		direction: 'both',
		decomposition: 'allocated',
		compute: { kind: 'ratio_allocated' },
		allocationBasis: RIPARTO_KWH,
	},
	{
		id: 'energia_incentivabile',
		label: 'Energia incentivabile',
		unit: 'kWh',
		direction: 'both',
		decomposition: 'none',
		compute: { kind: 'ratio_allocated' },
		noExplodeReason:
			"L'esclusione della quota da impianti preesistenti si applica al totale di comunità, " +
			'sul lato produzione: non esiste una quota incentivabile del singolo POD in prelievo. ' +
			"Per la scomposizione usa l'energia condivisa.",
	},
	{
		id: 'risparmio_ai',
		label: 'Risparmio dalle azioni AI',
		unit: '€',
		direction: 'both',
		decomposition: 'none',
		compute: { kind: 'ratio_allocated' },
		noExplodeReason: EURO_NON_PER_POD,
	},

	// ── Previsione ────────────────────────────────────────────────────────────
	// Il livello 4 (`estimated_data`, la curva tipica materializzata) è per
	// misuratore, quindi la previsione si scompone per POD come una misura — ma
	// resta una previsione, e l'esploso lo dice.
	{
		id: 'forecast_produzione_7gg',
		label: 'Produzione prevista · 7 giorni',
		unit: 'kWh',
		direction: 'produzione',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'day' },
		note: 'Previsione dal livello stimato, non misura.',
	},
	{
		id: 'forecast_consumo_7gg',
		label: 'Consumo previsto · 7 giorni',
		unit: 'kWh',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'day' },
		note: 'Previsione dal livello stimato, non misura.',
	},
	{
		id: 'forecast_profilo_orario',
		label: 'Profilo orario previsto',
		unit: 'kWh',
		direction: 'both',
		decomposition: 'sum',
		compute: { kind: 'energy_series', bucket: 'hour' },
		note: 'Previsione dal livello stimato, non misura.',
	},
	{
		id: 'forecast_accuratezza',
		label: 'Accuratezza del modello',
		unit: '%',
		direction: 'both',
		decomposition: 'sum',
		compute: { kind: 'layer_delta', left: 'stimato', right: 'reale' },
		note: 'Scostamento fra livello stimato e livello misurato, per POD.',
	},

	// ── Anomalie ──────────────────────────────────────────────────────────────
	{
		id: 'anomalie_aperte',
		label: 'Anomalie aperte',
		unit: 'n',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'row_count' },
	},
	{
		id: 'anomalie_pod_coinvolti',
		label: 'POD coinvolti',
		unit: 'n',
		direction: 'consumo',
		decomposition: 'sum',
		compute: { kind: 'row_count' },
	},

	// ── Pannelli che non si esplodono, e perché ────────────────────────────────
	{
		id: 'meteo_live',
		label: 'Meteo live',
		unit: 'n',
		direction: 'both',
		decomposition: 'none',
		compute: { kind: 'row_count' },
		noExplodeReason: 'Il meteo è di sito, non di punto di connessione.',
	},
	{
		id: 'piattaforma_conteggi',
		label: 'Conteggi di piattaforma',
		unit: 'n',
		direction: 'both',
		decomposition: 'none',
		compute: { kind: 'row_count' },
		noExplodeReason: 'Conteggi su tutte le CER: un singolo POD non ne è una quota.',
	},
	{
		id: 'stato_gse_arera',
		label: 'Stato GSE / ARERA',
		unit: 'n',
		direction: 'both',
		decomposition: 'none',
		compute: { kind: 'row_count' },
		noExplodeReason: 'Adempimenti della configurazione, non del singolo POD.',
	},
] as const;

const BY_ID = new Map(METRICS.map((m) => [m.id, m]));

/** La metrica, o `undefined` se l'id non è nel registro. */
export function getMetric(id: string): MetricDef | undefined {
	return BY_ID.get(id);
}

/** Esito della domanda «questa metrica si può leggere un livello per volta?». */
export interface LayerFilterSupport {
	enabled: boolean;
	/** Presente solo quando `enabled` è falso: perché il filtro non si applica. */
	reason?: string;
}

/**
 * Se la classifica può essere riletta su un singolo livello energetico.
 *
 * Il filtro per livello sostituisce il valore riconciliato con la somma di
 * quella sola sorgente, quindi ha senso esattamente dove il valore **è** una
 * somma di kWh. Dove il numero nasce da una derivazione (CAGR, fasce), da un
 * confronto fra livelli o da un riparto calcolato sul riconciliato, mostrare
 * «lo stesso KPI sul livello GSE» significherebbe cambiargli definizione senza
 * dirlo — e un numero che cambia definizione a ogni click è peggio di un
 * filtro assente. Qui si dichiara quale dei due casi è, e perché.
 */
export function layerFilterSupport(metric: MetricDef): LayerFilterSupport {
	switch (metric.compute.kind) {
		case 'energy_sum':
		case 'energy_series':
			return { enabled: true };
		case 'energy_derived':
			return {
				enabled: false,
				reason:
					'Il valore è una derivazione sulla serie riconciliata (CAGR, anno su anno, medie, ' +
					'fasce): su un livello solo non sarebbe lo stesso indicatore.',
			};
		case 'layer_delta':
			return { enabled: false, reason: 'La metrica confronta già due livelli fra loro.' };
		case 'ratio_allocated':
			return {
				enabled: false,
				reason:
					'Il valore è una quota ripartita a partire dal riconciliato: un singolo livello ' +
					'non ha una ripartizione propria.',
			};
		case 'row_count':
			return { enabled: false, reason: 'Il valore è un conteggio di righe, non energia.' };
	}
}

/**
 * Se un POD senza dati nell'intervallo va elencato a 0 invece che omesso.
 *
 * Su una somma di energia, su un conteggio e su una quota ripartita lo zero è
 * un'informazione vera — quel POD non ha prodotto, non ha anomalie, non ha
 * ricevuto nulla — e vederlo in classifica è il modo per accorgersi di un
 * misuratore muto. Su una derivazione o su uno scostamento fra livelli lo zero
 * sarebbe invece una risposta inventata: «0% di crescita» e «previsione
 * perfetta» si leggono come misure, non come assenza di dati, quindi lì il POD
 * resta fuori.
 */
export function listsPodsWithoutData(metric: MetricDef): boolean {
	const { kind } = metric.compute;
	return kind !== 'energy_derived' && kind !== 'layer_delta';
}

/** `true` se il pannello può mostrare un esploso per POD. */
export function isExplodable(id: string): boolean {
	const m = BY_ID.get(id);
	return m != null && m.decomposition !== 'none';
}

/** Gli id noti, per validare un parametro in ingresso senza esporre la mappa. */
export function metricIds(): string[] {
	return [...BY_ID.keys()];
}
